import { typeCheck } from '../lib/util';
import productService from './productService';
import Product from './Product';
import PaginatedList from '../Pagination/PaginatedList';
import PaginationOptions from '../Pagination/PaginationOptions';
import RemotePaginatedList from '../Pagination/RemotePaginatedList';

/**
 * SDK interface for working with Collaterate Products.
 *
 * @namespace Collaterate.Product
 */

export default class ProductAPI {

    /**
     * Search for a paginated list of products.
     * @param {Collaterate.Product~productSearchOptions} [productSearchOptions] - Options object containing product search criteria.
     * @param {PaginatedList~paginationOptions} [paginationOptions] - Object of options to control paginated response.
     * @returns {Promise<PaginatedList>} Returns a Promise that resolves with a {@link PaginatedList PaginatedList}, whose items are {@link Product Product} objects.
     * @memberof Collaterate.Product
     * @example
     * // Get a paginated list of products, store the PaginatedList response, and log the products to the console
     * let paginatedProductList;
     *
     * Collaterate.Product.search({ keyword: 'booklet' }, { page: 2 })
     *     .then(paginatedList => {
     *         paginatedProductList = paginatedList;
     *     })
     *     .then(() => {
     *         paginatedProductList.items.forEach(console.log);
     *     });
     */

    static async search (productSearchOptions, paginationOptions) {
        productSearchOptions && typeCheck(productSearchOptions, Object);
        paginationOptions && typeCheck(paginationOptions, Object);

        const pageOptions = new PaginationOptions({
            sort: {
                fieldName: 'name',
                ascending: true,
            },
            ...paginationOptions
        });

        const { content, page } = await productService.search({
            ...productSearchOptions,
            page: pageOptions,
        });

        return new PaginatedList(new RemotePaginatedList(content.map(item => new Product(item)), page, this.search.bind(this, productSearchOptions)))
    }

    /**
     * Get one product, such as the one a product page shows, by its idWithPrefix.
     * @param {string} idWithPrefix - The product's unique id, such as "SLS_135008".
     * @returns {Promise<?Product>} Returns a Promise that resolves with the {@link Product Product}, or with null when the
     *     site has no such product the user can see.
     * @memberof Collaterate.Product
     * @example
     * // Show the chosen variant's photo on a stock product page
     * Collaterate.Product.getById(stockGlobals.idWithPrefix)
     *     .then(product => product && product.images.filter(image => image.variantIds.length))
     *     .then(console.log);
     */
    static async getById (idWithPrefix) {
        typeCheck(idWithPrefix, String);

        const { content } = await productService.search({
            idsWithPrefix: [idWithPrefix],
            page: new PaginationOptions({ page: 1, pageSize: 1 }),
        });

        // A platform that predates the idsWithPrefix filter ignores it and answers with other products, so check.
        const match = (content || []).find(item => item.idWithPrefix === idWithPrefix);
        return match ? new Product(match) : null;
    }
}

/**
 * Product search options object.
 *
 * @typedef {Object} Collaterate.Product~productSearchOptions
 * @property {string} [keyword] - Key word or phrase to search for.
 * @property {number[]} [categoryIds] - Array of categories to fetch. Omit to search for all categories.
 * @property {string[]} [idsWithPrefix] - Only these products, by idWithPrefix. See also {@link Collaterate.Product.getById getById}.
 */
